import { getPref, setPref } from '../persistentPrefs';
import { isWeb } from '../platform';
import { getEffectiveMusicVolume } from './soundSettings';
import { setMusicSuppressed } from './musicManager';
import { attachUnderwaterFilter, detachUnderwaterFilter } from './underwaterFilter';

const PREFS_KEY = 'valkenhall-custom-music';

/**
 * Single owner of custom-music playback state in the renderer.
 *
 * Tracks come from a user-chosen folder watched by the main process.
 * The renderer holds a single `<Audio>` element and toggles
 * `setMusicSuppressed(true)` on the built-in music manager whenever a
 * custom track plays. An SSE connection to `/api/custom-music/events`
 * pushes `library-changed` notifications so the in-game list reflects
 * folder edits immediately, without polling.
 *
 * Volume model: custom tracks share the music channel. See
 * `getEffectiveMusicVolume()`. Per-track `gain` (0–1.5) tunes loudness.
 */

const state = {
  library: {
    folderPath: null,
    folderMissing: false,
    libraryLoop: false,
    tracks: [],
    totalBytes: 0,
  },
  currentTrackId: null,
  isPlaying: false,
  isLoading: false,
  currentTimeSec: 0,
  durationSec: 0,
  error: null,
};

let audio = null;
let hydrated = false;
let hydratePromise = null;
let recentErrorIds = new Set();
let eventSource = null;
const listeners = new Set();
// Subsurface region view fades the user's custom track out while
// active, then back in on exit. We keep the audio element alive (so
// the resume picks up at the same position) and remember whether it
// was actually playing before the suppression.
let externalSuppressed = false;
let resumeOnUnsuppress = false;
let fadeTimer = null;
// Transient duck applied while a notification plays. Always returns to 1;
// `destroyAudio` resets it so a duck interrupted by a track change can't
// leave the next track permanently quiet.
let duckFactor = 1;
let duckTimer = null;
// Active "far-away" filter amount (0 = clean, 1 = full underground
// hall). Applied to every audio element created here so newly
// started tracks pick up the current state seamlessly.
let underwaterAmount = 0;

function notify() {
  for (const fn of listeners) {
    try { fn(); } catch {}
  }
}

function persistPlayback() {
  setPref(PREFS_KEY, {
    currentTrackId: state.currentTrackId,
    isPlaying: state.isPlaying,
  });
}

function trackById(id) {
  return state.library.tracks.find((t) => t.id === id) || null;
}

function effectiveVolumeFor(track) {
  if (!track) return 0;
  const base = getEffectiveMusicVolume();
  const trackGain = typeof track.gain === 'number' ? track.gain : 1;
  return Math.max(0, Math.min(1, base * trackGain)) * duckFactor;
}

function currentEffectiveVolume() {
  return effectiveVolumeFor(trackById(state.currentTrackId));
}

/**
 * Dip the user's track briefly so a notification can land on top of it, then
 * bring it back. Fast down, slow up. See `duckAmbience` for why.
 *
 * Skipped while suppressed or mid-fade, because both already own `volume`.
 */
export function duckCustomMusic({ depth = 0.35, downMs = 180, holdMs = 900, upMs = 1600 } = {}) {
  if (!audio || audio.paused || externalSuppressed || fadeTimer) return;
  const track = trackById(state.currentTrackId);
  if (effectiveVolumeFor(track) <= 0) return;
  clearTimeout(duckTimer);
  duckFactor = depth;
  fadeAudioTo(currentEffectiveVolume, downMs, () => {
    duckTimer = setTimeout(() => {
      duckFactor = 1;
      fadeAudioTo(currentEffectiveVolume, upMs);
    }, holdMs);
  });
}

function destroyAudio() {
  if (!audio) return;
  clearFadeTimer();
  clearTimeout(duckTimer);
  duckTimer = null;
  duckFactor = 1;
  detachUnderwaterFilter(audio);
  try { audio.pause(); } catch {}
  try { audio.src = ''; } catch {}
  audio = null;
}

function clearFadeTimer() {
  if (fadeTimer) {
    clearInterval(fadeTimer);
    fadeTimer = null;
  }
}

// `to` may be a getter: a ramp toward the user's level re-reads it every
// tick, so a settings change mid-fade lands on the new level.
function fadeAudioTo(to, durationMs, onDone) {
  if (!audio) { if (onDone) onDone(); return; }
  clearFadeTimer();
  const start = audio.volume;
  const steps = 30;
  const stepMs = Math.max(8, Math.floor(durationMs / steps));
  let i = 0;
  fadeTimer = setInterval(() => {
    i++;
    if (!audio) { clearFadeTimer(); if (onDone) onDone(); return; }
    const target = typeof to === 'function' ? to() : to;
    if (i >= steps) {
      audio.volume = target;
      clearFadeTimer();
      if (onDone) onDone();
      return;
    }
    audio.volume = start + (target - start) * (i / steps);
  }, stepMs);
}

/**
 * Re-fetch the library from the server. The watcher's SSE stream
 * triggers this on every external folder change.
 */
async function refreshLibrary({ silent = false } = {}) {
  try {
    const res = await fetch('/api/custom-music/library');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    state.library = {
      folderPath: typeof data.folderPath === 'string' ? data.folderPath : null,
      folderMissing: Boolean(data.folderMissing),
      libraryLoop: Boolean(data.libraryLoop),
      tracks: Array.isArray(data.tracks) ? data.tracks : [],
      totalBytes: typeof data.totalBytes === 'number' ? data.totalBytes : 0,
    };
    state.error = null;
    reconcilePlaybackWithLibrary();
  } catch (err) {
    state.error = err?.message || 'Failed to load library';
  }
  if (!silent) notify();
}

/**
 * If the currently-playing track disappears from the library (e.g. user
 * removed the file from the folder), stop playback so we don't keep a
 * stale audio element pinned and the built-in music can resume.
 */
function reconcilePlaybackWithLibrary() {
  if (!state.currentTrackId) return;
  if (!trackById(state.currentTrackId)) {
    destroyAudio();
    state.currentTrackId = null;
    state.isPlaying = false;
    state.currentTimeSec = 0;
    state.durationSec = 0;
    setMusicSuppressed(false);
    persistPlayback();
  }
}

function connectEventStream() {
  if (eventSource || typeof EventSource === 'undefined') return;
  try {
    const es = new EventSource('/api/custom-music/events');
    es.onmessage = (evt) => {
      let data;
      try { data = JSON.parse(evt.data); } catch { return; }
      if (data?.type === 'library-changed') {
        refreshLibrary().catch(() => {});
      }
    };
    es.onerror = () => {
      // Built-in EventSource auto-reconnects; nothing to do.
    };
    eventSource = es;
  } catch {}
}

function disconnectEventStream() {
  if (!eventSource) return;
  try { eventSource.close(); } catch {}
  eventSource = null;
}

export async function hydrateCustomMusic() {
  // Custom music is backed by a native folder picker + fs watcher in the
  // Electron main process. On web: never hydrate, never open the SSE
  // stream (EventSource would retry a 404 endpoint forever).
  if (isWeb()) { hydrated = true; return; }
  if (hydrated) return;
  if (hydratePromise) return hydratePromise;
  hydratePromise = (async () => {
    const stored = getPref(PREFS_KEY, null);
    await refreshLibrary({ silent: true });

    if (stored && stored.currentTrackId && stored.isPlaying) {
      const track = trackById(stored.currentTrackId);
      if (track) {
        startPlayback(track);
      } else {
        state.currentTrackId = null;
        state.isPlaying = false;
        persistPlayback();
      }
    }
    connectEventStream();
    hydrated = true;
    notify();
  })();
  return hydratePromise;
}

function startPlayback(track) {
  destroyAudio();
  state.currentTrackId = track.id;
  state.isPlaying = true;
  state.currentTimeSec = 0;
  state.durationSec = typeof track.durationSeconds === 'number' ? track.durationSeconds : 0;
  recentErrorIds = new Set();

  const el = new Audio(`/custom-music/file/${track.id}`);
  el.loop = Boolean(track.loop);
  el.volume = effectiveVolumeFor(track);
  el.preload = 'auto';
  // Required for createMediaElementSource. Without this the audio's
  // media is tainted CORS-wise and the Web Audio chain throws on attach.
  el.crossOrigin = 'anonymous';

  // Wire through the underwater filter and snap to the current
  // amount so a track started while subsurface is active sounds
  // already-distant from the first frame.
  attachUnderwaterFilter(el).setAmount(underwaterAmount, 0);

  el.addEventListener('ended', () => {
    if (audio !== el) return;
    if (el.loop) return;
    playNext();
  });
  el.addEventListener('error', () => {
    if (audio !== el) return;
    handlePlaybackError(track.id);
  });
  el.addEventListener('loadedmetadata', () => {
    if (audio !== el) return;
    if (Number.isFinite(el.duration) && el.duration > 0) {
      state.durationSec = el.duration;
      notify();
      if (!track.durationSeconds) {
        patchTrackQuietly(track.id, { durationSeconds: el.duration });
      }
    }
  });
  el.addEventListener('timeupdate', () => {
    if (audio !== el) return;
    const next = el.currentTime || 0;
    if (Math.abs(next - state.currentTimeSec) >= 0.25 || next === 0) {
      state.currentTimeSec = next;
      notify();
    }
  });

  audio = el;
  setMusicSuppressed(true);

  el.play().catch(() => {
    if (el.error) return;
    let retryTimer = null;
    const resume = () => {
      if (audio !== el) {
        cleanup();
        return;
      }
      if (el.paused) {
        el.play().then(cleanup).catch(() => {});
      } else {
        cleanup();
      }
    };
    const cleanup = () => {
      clearInterval(retryTimer);
      retryTimer = null;
      document.removeEventListener('click', resume);
      document.removeEventListener('keydown', resume);
      document.removeEventListener('pointerdown', resume);
      document.removeEventListener('mousemove', resume);
    };
    retryTimer = setInterval(resume, 500);
    document.addEventListener('click', resume, { once: true });
    document.addEventListener('keydown', resume, { once: true });
    document.addEventListener('pointerdown', resume, { once: true });
    document.addEventListener('mousemove', resume, { once: true });
  });

  persistPlayback();
  notify();
}

function handlePlaybackError(trackId) {
  if (recentErrorIds.has(trackId)) {
    stopTrack();
    return;
  }
  recentErrorIds.add(trackId);
  if (recentErrorIds.size >= state.library.tracks.length) {
    stopTrack();
    return;
  }
  playNext();
}

async function patchTrackQuietly(id, body) {
  try {
    await fetch(`/api/custom-music/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    await refreshLibrary({ silent: true });
  } catch {}
}

export function getCustomMusicState() {
  return {
    library: state.library,
    currentTrackId: state.currentTrackId,
    isPlaying: state.isPlaying,
    isLoading: state.isLoading,
    currentTimeSec: state.currentTimeSec,
    durationSec: state.durationSec,
    error: state.error,
  };
}

export function subscribeCustomMusic(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function playTrack(id) {
  const track = trackById(id);
  if (!track) return;
  if (state.currentTrackId === id && audio && !audio.paused) return;

  if (state.currentTrackId === id && audio) {
    audio.volume = effectiveVolumeFor(track);
    audio.play().catch(() => {});
    state.isPlaying = true;
    setMusicSuppressed(true);
    persistPlayback();
    notify();
    return;
  }
  startPlayback(track);
}

export function pauseTrack() {
  if (!audio) {
    state.isPlaying = false;
    setMusicSuppressed(false);
    persistPlayback();
    notify();
    return;
  }
  try { audio.pause(); } catch {}
  state.isPlaying = false;
  setMusicSuppressed(false);
  persistPlayback();
  notify();
}

export function stopTrack() {
  destroyAudio();
  state.currentTrackId = null;
  state.isPlaying = false;
  state.currentTimeSec = 0;
  state.durationSec = 0;
  setMusicSuppressed(false);
  persistPlayback();
  notify();
}

export function seekTo(seconds) {
  if (!audio) return;
  const target = Math.max(0, Number(seconds) || 0);
  const duration = Number.isFinite(audio.duration) && audio.duration > 0
    ? audio.duration
    : state.durationSec;
  const clamped = duration > 0 ? Math.min(target, duration) : target;
  try {
    audio.currentTime = clamped;
  } catch {
    return;
  }
  state.currentTimeSec = clamped;
  notify();
}

export function playNext() {
  const tracks = state.library.tracks;
  if (!tracks.length) {
    stopTrack();
    return;
  }
  const currentIdx = tracks.findIndex((t) => t.id === state.currentTrackId);
  let nextIdx = currentIdx + 1;
  if (nextIdx >= tracks.length) {
    if (!state.library.libraryLoop) {
      stopTrack();
      return;
    }
    nextIdx = 0;
  }
  startPlayback(tracks[nextIdx]);
}

export function playPrevious() {
  const tracks = state.library.tracks;
  if (!tracks.length) return;
  const currentIdx = tracks.findIndex((t) => t.id === state.currentTrackId);
  const prevIdx = Math.max(0, currentIdx - 1);
  const candidate = tracks[prevIdx];
  if (candidate) startPlayback(candidate);
}

/**
 * Open the native folder picker. Returns the chosen folder path, or
 * null if the user cancelled.
 */
export async function pickFolder() {
  state.isLoading = true;
  notify();
  try {
    const res = await fetch('/api/custom-music/folder', { method: 'POST' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Folder pick failed: ${res.status}`);
    }
    const data = await res.json();
    if (data.cancelled) return null;
    await refreshLibrary({ silent: true });
    return data.folderPath || null;
  } finally {
    state.isLoading = false;
    notify();
  }
}

export async function clearFolder() {
  stopTrack();
  await fetch('/api/custom-music/folder', { method: 'DELETE' });
  await refreshLibrary();
}

export async function renameTrack(id, displayName) {
  const optimistic = trackById(id);
  if (optimistic) {
    optimistic.displayName = displayName;
    notify();
  }
  await fetch(`/api/custom-music/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ displayName }),
  });
  await refreshLibrary();
}

export async function setTrackLoop(id, loop) {
  const track = trackById(id);
  if (track) {
    track.loop = Boolean(loop);
    if (audio && state.currentTrackId === id) audio.loop = Boolean(loop);
    notify();
  }
  await fetch(`/api/custom-music/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ loop: Boolean(loop) }),
  });
  await refreshLibrary({ silent: true });
}

export async function setTrackGain(id, gain) {
  const clamped = Math.max(0, Math.min(1.5, Number(gain) || 0));
  const track = trackById(id);
  if (track) {
    track.gain = clamped;
    if (audio && state.currentTrackId === id) audio.volume = effectiveVolumeFor(track);
    notify();
  }
  await fetch(`/api/custom-music/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ gain: clamped }),
  });
  await refreshLibrary({ silent: true });
}

export async function reorderTracks(ids) {
  state.library.tracks = ids
    .map((id) => state.library.tracks.find((t) => t.id === id))
    .filter(Boolean);
  notify();
  await fetch('/api/custom-music/order', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  await refreshLibrary({ silent: true });
}

export async function setLibraryLoop(value) {
  state.library.libraryLoop = Boolean(value);
  notify();
  await fetch('/api/custom-music/settings', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ libraryLoop: Boolean(value) }),
  });
}

export function updateCustomMusicVolume() {
  if (!audio) return;
  if (externalSuppressed || fadeTimer) return;
  const track = trackById(state.currentTrackId);
  audio.volume = effectiveVolumeFor(track);
}

/**
 * Drive the "far away, underground hall" effect on the user's
 * custom track. Same shape as setMusicUnderwater in musicManager.
 * Keeps playback going but ramps in a lowpass-muffle + convolver
 * reverb so the music sounds distant. `amount` 0..1, `fadeMs` is
 * the ramp duration.
 */
export function setCustomMusicUnderwater(amount, fadeMs = 1500) {
  underwaterAmount = Math.max(0, Math.min(1, amount));
  if (audio) {
    attachUnderwaterFilter(audio).setAmount(underwaterAmount, fadeMs);
  }
}

/**
 * Fade out + pause the current custom track without losing position,
 * or fade in + resume from where it left off. Used by the subsurface
 * region view so the cave + underwater ambience takes the music
 * channel briefly. `state.isPlaying` stays truthful to the user's
 * intent so the in-game music UI keeps showing "playing".
 */
export function setCustomMusicSuppressed(value, { fadeMs = 1500 } = {}) {
  const next = Boolean(value);
  if (next === externalSuppressed) return;
  externalSuppressed = next;
  if (next) {
    resumeOnUnsuppress = Boolean(audio && state.isPlaying && !audio.paused);
    if (audio) {
      fadeAudioTo(0, fadeMs, () => {
        if (externalSuppressed && audio) {
          try { audio.pause(); } catch {}
        }
      });
    }
    return;
  }
  if (!resumeOnUnsuppress || !audio) {
    resumeOnUnsuppress = false;
    return;
  }
  resumeOnUnsuppress = false;
  audio.volume = 0;
  audio.play().catch(() => {});
  fadeAudioTo(currentEffectiveVolume, fadeMs);
}

export function __resetForTests() {
  destroyAudio();
  disconnectEventStream();
  state.library = {
    folderPath: null,
    folderMissing: false,
    libraryLoop: false,
    tracks: [],
    totalBytes: 0,
  };
  state.currentTrackId = null;
  state.isPlaying = false;
  state.isLoading = false;
  state.currentTimeSec = 0;
  state.durationSec = 0;
  state.error = null;
  hydrated = false;
  hydratePromise = null;
  recentErrorIds = new Set();
  listeners.clear();
  externalSuppressed = false;
  resumeOnUnsuppress = false;
  underwaterAmount = 0;
  setMusicSuppressed(false);
}
